import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useAuth } from '@/lib/auth/auth-context';

/**
 * Sign-out held because some answers could not be sent (#119 NW1, user
 * decision 2026-10-23). `logout` first sends every unsent answer while the
 * session is still valid; when that fails or takes longer than a few seconds,
 * this asks, with the application form's own "You have unsaved answers" copy:
 *
 * - **Stay on this page** cancels the sign-out; the answers keep saving.
 * - **Sign out anyway** discards them and signs out.
 *
 * Mounted once beside the router, so every sign-out button gets it. While
 * the answers are being sent it shows a small "Sending your answers…" status
 * (NI-F).
 */
export function SignOutUnsavedDialog() {
  const { t } = useTranslation();
  const { signOutHeld, signOutSending, cancelSignOut, logout } = useAuth();
  return (
    <>
      {signOutSending ? (
        // NI-F: the menu has closed and the send can take a few seconds; say so.
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-xl border bg-background px-4 py-2 text-sm shadow-lg"
        >
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {/* Sending your answers… */ t('auth.signOutUnsaved.sending')}
        </div>
      ) : null}
      <Modal
        open={signOutHeld}
        onClose={cancelSignOut}
        title={/* You have unsaved answers */ t('appform.leave.title')}
        description={/* Some of your answers haven’t reached the server yet. If you leave now, those answers will be lost. */ t('appform.leave.body')}
      >
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => void logout({ force: true })}>
            {/* Sign out anyway */ t('auth.signOutUnsaved.signOut')}
          </Button>
          <Button type="button" variant="brand" data-autofocus onClick={cancelSignOut}>
            {/* Stay on this page */ t('auth.signOutUnsaved.stay')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
